import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Edit3, X, Repeat2, Lock, Mail, CalendarClock, Send, FolderPlus, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import api, { backendUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LocationFields from "@/components/LocationFields";
import { LEADS } from "@/constants/testIds/crm";
import { getStatusLabel, getStatusToneClasses } from "@/lib/statusStyles";
import { normalizeListResponse } from "@/lib/listResponse";
import StatusCountCards from "@/components/data-management/StatusCountCards";
import PaginationBar from "@/components/data-management/PaginationBar";

const STATUSES = [
    { v: "new_lead", label: "New Lead" },
    { v: "contacted", label: "Contacted" },
    { v: "follow_up", label: "Follow-up" },
    { v: "meeting_scheduled", label: "Meeting Scheduled" },
    { v: "proposal_sent", label: "Proposal Sent" },
    { v: "negotiation", label: "Negotiation" },
    { v: "won", label: "Won" },
    { v: "lost", label: "Lost" },
];
const PRIORITIES = ["low", "medium", "high", "urgent"];
const SOURCES = ["upwork", "website", "referral", "other"];
const PRIORITY_STYLE = {
    urgent: "bg-destructive/10 text-destructive border-destructive/30",
    high: "bg-warning/10 text-warning border-warning/30",
    medium: "bg-primary/10 text-primary border-primary/30",
    low: "bg-muted text-muted-foreground border-border",
};

function StatusPill({ v }) {
    const label = STATUSES.find((s) => s.v === v)?.label || getStatusLabel(v);
    return <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-medium border ${getStatusToneClasses(v, { group: "status", variant: "badge" })}`}>{label}</span>;
}

function PriorityPill({ v }) {
    return <span className={`inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold border ${PRIORITY_STYLE[v]}`}>{v}</span>;
}

export default function Leads() {
    const { isAdmin } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [counts, setCounts] = useState({});
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(1);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [priority, setPriority] = useState("");
    const [source, setSource] = useState("");
    const [sort, setSort] = useState("-created_at");
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [detail, setDetail] = useState(null); // lead being viewed in drawer
    const limit = 10;

    const load = useCallback(async () => {
        const params = new URLSearchParams({ page, limit, sort });
        if (q) params.set("q", q);
        if (status) params.set("status", status);
        if (priority) params.set("priority", priority);
        if (source) params.set("source", source);
        const { data } = await api.get(`/leads?${params.toString()}`);
        const normalized = normalizeListResponse(data);
        setItems(normalized.items);
        setTotal(normalized.total);
        setCounts(normalized.counts);
    }, [page, q, status, priority, source, sort]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/users").then((r) => setUsers(normalizeListResponse(r.data).items)).catch(() => setUsers([]));
    }, []);
    useEffect(() => {
        const leadId = searchParams.get("lead");
        if (!leadId) return;
        const matched = items.find((l) => l.id === leadId);
        if (matched) {
            setDetail(matched);
            return;
        }
        api.get(`/leads/${leadId}`)
            .then((r) => setDetail(r.data))
            .catch(() => toast.error("Lead not found"));
    }, [searchParams, items]);

    const openCreate = () => { setEditing(null); setFormOpen(true); };
    const openEdit = (lead) => { setEditing(lead); setFormOpen(true); };
    const closeDetail = () => {
        setDetail(null);
        if (searchParams.get("lead")) {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete("lead");
                return next;
            }, { replace: true });
        }
    };

    const remove = async (lead) => {
        if (!window.confirm(`Delete lead "${lead.company}"?`)) return;
        await api.delete(`/leads/${lead.id}`);
        toast.success("Lead deleted");
        load();
    };

    const statusCards = STATUSES.map((s) => ({
        label: s.label,
        value: counts[s.v] || 0,
        hint: `${s.label} leads`,
    }));

    return (
        <div className="space-y-6" data-testid={LEADS.root}>
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Directory</div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Leads</h1>
                    <p className="text-sm text-muted-foreground mt-2">All prospects — search, filter and act.</p>
                </div>
                <button
                    onClick={openCreate}
                    data-testid={LEADS.createButton}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 active:scale-[0.98] transition-colors transition-transform"
                >
                    <Plus className="h-4 w-4" /> New Lead
                </button>
            </div>

            <StatusCountCards
                className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3"
                cards={[
                    { label: "Total Leads", value: total, hint: "All matching records" },
                    ...statusCards,
                ]}
            />

            {/* Filter bar */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <input
                    data-testid={LEADS.searchInput}
                    value={q}
                    onChange={(e) => { setPage(1); setQ(e.target.value); }}
                    placeholder="Search company, client, email…"
                    className="col-span-2 h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors"
                />
                <select data-testid={LEADS.statusFilter} value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    <option value="">All statuses</option>
                    {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                </select>
                <select data-testid={LEADS.priorityFilter} value={priority} onChange={(e) => { setPage(1); setPriority(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    <option value="">All priorities</option>
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <select data-testid={LEADS.sourceFilter} value={source} onChange={(e) => { setPage(1); setSource(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    <option value="">All sources</option>
                    {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>

            {/* Table */}
            <div className="border border-border rounded-md bg-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-secondary/40 border-b border-border">
                            <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                                    {[
                                        { k: "company", label: "Company" },
                                        { k: "company_email", label: "Company Email" },
                                        { k: "client_name", label: "Client Name" },
                                        { k: "client_email", label: "Client Email" },
                                        { k: "phone", label: "Phone Number" },
                                        { k: "website", label: "Website" },
                                        { k: "status", label: "Status" },
                                        { k: "source", label: "Source" },
                                        { k: "next_follow_up", label: "Next Follow-up" },
                                        { k: "assigned_to", label: "Assigned User" },
                                        { k: "actions", label: "" },
                                    ].map((c) => (
                                        <th
                                            key={c.k}
                                            className={`px-4 py-3 font-semibold ${c.k !== "actions" ? "cursor-pointer hover:text-foreground" : ""}`}
                                            onClick={() => c.k !== "actions" && setSort((s) => s === `-${c.k}` ? c.k : `-${c.k}`)}
                                        >
                                        {c.label}
                                        {sort.replace("-", "") === c.k && (sort.startsWith("-") ? " ↓" : " ↑")}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={11} className="text-center py-10 text-muted-foreground">No leads found.</td></tr>
                            )}
                            {items.map((l) => (
                                <tr
                                    key={l.id}
                                    data-testid={LEADS.tableRow}
                                    onClick={() => setDetail(l)}
                                    className={`border-b border-border last:border-0 hover:brightness-[0.99] cursor-pointer transition-colors ${getStatusToneClasses(l.status, { group: "status", variant: "row" })}`}
                                >
                                    <td className="px-4 py-3 font-medium">{l.company}</td>
                                    <td className="px-4 py-3 text-muted-foreground text-xs font-mono">{l.company_email || "—"}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{l.client_name}</td>
                                    <td className="px-4 py-3 text-muted-foreground text-xs font-mono">{l.client_email || l.email || "—"}</td>
                                    <td className="px-4 py-3 text-muted-foreground text-xs font-mono">{l.phone || "—"}</td>
                                    <td className="px-4 py-3 text-muted-foreground text-xs truncate max-w-[160px]">{l.website || "—"}</td>
                                    <td className="px-4 py-3"><StatusPill v={l.status} /></td>
                                    <td className="px-4 py-3 text-muted-foreground uppercase text-[11px] tracking-wider">{l.source}</td>
                                    <td className="px-4 py-3 text-muted-foreground text-xs font-mono">
                                        {l.next_follow_up ? new Date(l.next_follow_up).toLocaleDateString() : "—"}
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground text-xs">{users.find((u) => u.id === l.assigned_to)?.name || "—"}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                            <button onClick={() => openEdit(l)} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-secondary" aria-label="Edit">
                                                <Edit3 className="h-3.5 w-3.5" />
                                            </button>
                                            {isAdmin ? (
                                                <button data-testid={LEADS.deleteButton} onClick={() => remove(l)} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-destructive/10 text-destructive" aria-label="Delete">
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            ) : (
                                                <span title="Delete requires admin role" className="h-7 w-7 inline-flex items-center justify-center rounded text-muted-foreground/50 cursor-not-allowed">
                                                    <Lock className="h-3.5 w-3.5" />
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <PaginationBar
                    total={total}
                    page={page}
                    limit={limit}
                    onPageChange={setPage}
                    className=""
                />
            </div>

            {/* Form dialog */}
            <AnimatePresence>
                {formOpen && (
                    <LeadFormDialog
                        editing={editing}
                        users={users}
                        onClose={() => setFormOpen(false)}
                        onSaved={() => { setFormOpen(false); load(); }}
                    />
                )}
                {detail && (
                    <LeadDrawer
                        lead={detail}
                        users={users}
                        onClose={closeDetail}
                        onChange={load}
                        onEdit={openEdit}
                        onLeadUpdated={(updated) => {
                            setDetail(updated);
                            load();
                        }}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

function LeadFormDialog({ editing, users, onClose, onSaved }) {
    const [form, setForm] = useState(editing ? {
        company: editing.company || "",
        company_email: editing.company_email || editing.email || "",
        client_name: editing.client_name || "",
        client_email: editing.client_email || "",
        phone: editing.phone || "",
        website: editing.website || "",
        linkedin_url: editing.linkedin_url || "",
        country: editing.country || "",
        state: editing.state || "",
        city: editing.city || "",
        source: editing.source || "other",
        service: editing.service || "",
        status: editing.status || "new_lead",
        priority: editing.priority || "medium",
        value: editing.value || 0,
        assigned_to: editing.assigned_to || "",
        notes: editing.notes || "",
        next_follow_up: editing.next_follow_up || "",
    } : {
        company: "", company_email: "", client_name: "", client_email: "", phone: "", website: "", linkedin_url: "", country: "", state: "", city: "",
        source: "upwork", service: "", status: "new_lead", priority: "medium",
        value: 0, assigned_to: "", notes: "", next_follow_up: "",
    });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        const payload = { ...form, value: Number(form.value) || 0 };
        Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
        try {
            if (editing) await api.patch(`/leads/${editing.id}`, payload);
            else await api.post("/leads", payload);
            toast.success(editing ? "Lead updated" : "Lead created");
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const bind = (k) => ({ value: form[k] || "", onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <motion.form
                onSubmit={submit}
                initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 12, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-2xl bg-card border border-border rounded-md shadow-xl overflow-hidden"
            >
                <div className="flex items-center justify-between p-5 border-b border-border">
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">{editing ? "Edit" : "New"}</div>
                        <h3 className="font-heading text-xl font-semibold mt-0.5">{editing ? "Edit lead" : "Create lead"}</h3>
                    </div>
                    <button type="button" onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-secondary">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
                    <Field label="Company *"><input required data-testid={LEADS.formCompany} className={inputCls} {...bind("company")} /></Field>
                    <Field label="Company Email"><input type="email" data-testid={LEADS.formCompanyEmail} className={inputCls} {...bind("company_email")} /></Field>
                    <Field label="Client Name *"><input required data-testid={LEADS.formClientName} className={inputCls} {...bind("client_name")} /></Field>
                    <Field label="Client Email"><input type="email" data-testid={LEADS.formEmail} className={inputCls} {...bind("client_email")} /></Field>
                    <Field label="Phone"><input data-testid={LEADS.formPhone} className={inputCls} {...bind("phone")} /></Field>
                    <Field label="Website"><input data-testid={LEADS.formWebsite} className={inputCls} {...bind("website")} placeholder="acme.com" /></Field>
                    <Field label="LinkedIn Profile URL (optional)"><input data-testid={LEADS.formLinkedin || undefined} className={inputCls} {...bind("linkedin_url")} placeholder="https://linkedin.com/in/..." /></Field>
                    <LocationFields form={form} setForm={setForm} />
                    <Field label="Service"><input data-testid={LEADS.formService} className={inputCls} {...bind("service")} /></Field>
                    <Field label="Source">
                        <select data-testid={LEADS.formSource} className={inputCls} {...bind("source")}>
                            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </Field>
                    <Field label="Status">
                        <select data-testid={LEADS.formStatus} className={inputCls} {...bind("status")}>
                            {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                        </select>
                    </Field>
                    <Field label="Priority">
                        <select data-testid={LEADS.formPriority} className={inputCls} {...bind("priority")}>
                            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </Field>
                    <Field label="Value ($)"><input type="number" data-testid={LEADS.formValue} className={inputCls} {...bind("value")} /></Field>
                    <Field label="Assigned To">
                        <select data-testid={LEADS.formAssigned} className={inputCls} {...bind("assigned_to")}>
                            <option value="">— Unassigned —</option>
                            {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                        </select>
                    </Field>
                    <Field label="Next Follow-up">
                        <input type="datetime-local" className={inputCls}
                            value={form.next_follow_up ? form.next_follow_up.slice(0, 16) : ""}
                            onChange={(e) => setForm({ ...form, next_follow_up: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                        />
                    </Field>
                    <Field label="Notes" full>
                        <textarea rows={3} data-testid={LEADS.formNotes} className={inputCls} {...bind("notes")} />
                    </Field>
                </div>
                <div className="flex items-center justify-end gap-2 p-4 border-t border-border bg-secondary/20">
                    <button type="button" onClick={onClose} className="h-10 px-4 rounded-md border border-border hover:bg-secondary text-sm transition-colors">Cancel</button>
                    <button disabled={busy} data-testid={LEADS.formSubmit} className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 active:scale-[0.98] transition-colors transition-transform disabled:opacity-60">
                        {busy ? "Saving…" : (editing ? "Save changes" : "Create lead")}
                    </button>
                </div>
            </motion.form>
        </motion.div>
    );
}

const inputCls = "w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors";

function Field({ label, children, full }) {
    return (
        <label className={`space-y-1.5 ${full ? "md:col-span-2" : ""}`}>
            <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
            {children}
        </label>
    );
}

function LeadDrawer({ lead, users, onClose, onChange, onEdit, onLeadUpdated }) {
    const [tab, setTab] = useState("info");
    const [followups, setFollowups] = useState([]);
    const [proposals, setProposals] = useState([]);
    const [activities, setActivities] = useState([]);
    const [convertOpen, setConvertOpen] = useState(false);
    const [busyAction, setBusyAction] = useState(null);

    const load = useCallback(async () => {
        const [fu, pr, ac] = await Promise.all([
            api.get(`/followups?lead_id=${lead.id}`),
            api.get(`/proposals?lead_id=${lead.id}`),
            api.get(`/activities?entity_id=${lead.id}`),
        ]);
        setFollowups(fu.data);
        setProposals(pr.data);
        setActivities(ac.data);
    }, [lead.id]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    const assigned = users.find((u) => u.id === lead.assigned_to);
    const refreshLead = async (patch, label) => {
        setBusyAction(label);
        try {
            const { data } = await api.patch(`/leads/${lead.id}`, patch);
            toast.success(label);
            onLeadUpdated?.(data);
            onChange?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Action failed");
        } finally {
            setBusyAction(null);
        }
    };

    const createClientOnly = async () => {
        setBusyAction("convert-client");
        try {
            await api.post(`/leads/${lead.id}/client`);
            toast.success("Client created");
            onLeadUpdated?.({ ...lead, status: "won" });
            onChange?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create client");
        } finally {
            setBusyAction(null);
        }
    };

    const createProject = async () => {
        setBusyAction("create-project");
        try {
            await api.post(`/leads/${lead.id}/convert`, {
                project_name: `${lead.company} - Project`,
                budget: lead.value || 0,
                notes: lead.notes || "",
                team_members: [],
            });
            toast.success("Project created");
            onLeadUpdated?.({ ...lead, status: "won" });
            onChange?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create project");
        } finally {
            setBusyAction(null);
        }
    };

    const convertToWon = async () => refreshLead({ status: "won" }, "Marked as won");

    const sendQuickEmail = async () => {
        const recipient = lead.company_email || lead.client_email || lead.email;
        if (!recipient) {
            toast.error("No email address available");
            return;
        }
        window.location.href = `mailto:${recipient}?subject=${encodeURIComponent(`Checking in with ${lead.company}`)}`;
    };

    const sendProposal = async () => {
        setTab("proposals");
        toast("Use the proposal form below to send a proposal");
    };

    const deleteLead = async () => {
        if (!window.confirm(`Delete lead "${lead.company}"?`)) return;
        try {
            await api.delete(`/leads/${lead.id}`);
            toast.success("Lead deleted");
            onClose();
            onChange?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-background/70 backdrop-blur-sm"
            onClick={onClose}
        >
            <motion.aside
                initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }}
                data-testid={LEADS.detailDrawer}
                onClick={(e) => e.stopPropagation()}
                className="ml-auto h-full w-full max-w-xl bg-card border-l border-border overflow-y-auto"
            >
                <div className="p-6 border-b border-border sticky top-0 bg-card z-10">
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">{lead.source}</div>
                            <h3 className="font-heading text-2xl font-bold tracking-tight mt-1">{lead.company}</h3>
            <div className="text-sm text-muted-foreground">{lead.client_name} · {lead.client_email || lead.email || "—"}</div>
                        </div>
                        <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-secondary">
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="flex items-center gap-2 mt-4 flex-wrap">
                        <StatusPill v={lead.status} />
                        <span className="text-xs text-muted-foreground font-mono">{lead.company_email || "—"}</span>
                        {assigned && <span className="text-xs text-muted-foreground">· Assigned to {assigned.name}</span>}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <button onClick={() => onEdit?.(lead)} className="h-8 px-3 rounded-md border border-border hover:bg-secondary text-xs inline-flex items-center gap-1.5">
                            <Edit3 className="h-3.5 w-3.5" /> Edit Lead
                        </button>
                        <button onClick={() => setTab("followups")} className="h-8 px-3 rounded-md border border-border hover:bg-secondary text-xs inline-flex items-center gap-1.5">
                            <CalendarClock className="h-3.5 w-3.5" /> Schedule Follow-up
                        </button>
                        <button onClick={sendQuickEmail} className="h-8 px-3 rounded-md border border-border hover:bg-secondary text-xs inline-flex items-center gap-1.5" disabled={busyAction === "send-email"}>
                            <Mail className="h-3.5 w-3.5" /> Send Email
                        </button>
                        <button onClick={sendProposal} className="h-8 px-3 rounded-md border border-border hover:bg-secondary text-xs inline-flex items-center gap-1.5">
                            <Send className="h-3.5 w-3.5" /> Send Proposal
                        </button>
                        <button onClick={convertToWon} className="h-8 px-3 rounded-md border border-success/40 hover:bg-success/10 text-xs inline-flex items-center gap-1.5 text-success">
                            <ArrowRightLeft className="h-3.5 w-3.5" /> Convert to Won
                        </button>
                        <button onClick={createClientOnly} className="h-8 px-3 rounded-md border border-border hover:bg-secondary text-xs inline-flex items-center gap-1.5">
                            <FolderPlus className="h-3.5 w-3.5" /> Convert to Client
                        </button>
                        {lead.status === "won" && (
                            <button
                                data-testid={LEADS.convertButton}
                                onClick={createProject}
                                className="h-8 px-3 rounded-md bg-success text-white text-xs font-medium hover:bg-success/90 transition-colors inline-flex items-center gap-1.5"
                            >
                                <Repeat2 className="h-3.5 w-3.5" /> Create Project
                            </button>
                        )}
                        <button onClick={deleteLead} className="h-8 px-3 rounded-md border border-destructive/40 hover:bg-destructive/10 text-xs inline-flex items-center gap-1.5 text-destructive">
                            <Trash2 className="h-3.5 w-3.5" /> Delete Lead
                        </button>
                    </div>
                    <div className="flex items-center gap-1 mt-5 border-b border-border -mb-6 pb-0">
                        {["info", "followups", "proposals", "activity"].map((t) => (
                            <button
                                key={t}
                                onClick={() => setTab(t)}
                                className={`px-3 pb-2 text-sm border-b-2 -mb-px transition-colors ${
                                    tab === t ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"
                                }`}
                            >
                                {t === "followups" ? "Follow-ups" : t.charAt(0).toUpperCase() + t.slice(1)}
                                {t === "followups" && followups.length > 0 && <span className="ml-1 text-[10px] font-mono">({followups.length})</span>}
                                {t === "proposals" && proposals.length > 0 && <span className="ml-1 text-[10px] font-mono">({proposals.length})</span>}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="p-6 space-y-4">
                    {tab === "info" && <InfoTab lead={lead} />}
                    {tab === "followups" && <FollowupsTab leadId={lead.id} items={followups} reload={() => { load(); onChange && onChange(); }} />}
                    {tab === "proposals" && <ProposalsTab leadId={lead.id} items={proposals} reload={load} />}
                    {tab === "activity" && <ActivityTab items={activities} />}
                </div>
            </motion.aside>
            <AnimatePresence>
                {convertOpen && (
                    <ConvertDialog lead={lead} users={users} onClose={() => setConvertOpen(false)} onDone={() => { setConvertOpen(false); onChange && onChange(); toast.success("Converted to client & project"); }} />
                )}
            </AnimatePresence>
        </motion.div>
    );
}

function InfoTab({ lead }) {
    const rows = [
        ["Company", lead.company],
        ["Company Email", lead.company_email || "—"],
        ["Client", lead.client_name],
        ["Client Email", lead.client_email || lead.email || "—"],
        ["Phone", lead.phone || "—"],
        ["Website", lead.website || "—"],
        ["LinkedIn", lead.linkedin_url || "—"],
        ["Country", lead.country || "—"],
        ["State", lead.state || "—"],
        ["City", lead.city || "—"],
        ["Service", lead.service || "—"],
        ["Source", lead.source],
        ["Status", lead.status],
        ["Next Follow-up", lead.next_follow_up ? new Date(lead.next_follow_up).toLocaleString() : "—"],
        ["Notes", lead.notes || "—"],
    ];
    return (
        <div className="divide-y divide-border rounded-md border border-border overflow-hidden">
            {rows.map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-4 px-4 py-3 text-sm">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">{k}</div>
                    <div className="col-span-2 break-words">{v}</div>
                </div>
            ))}
        </div>
    );
}

function FollowupsTab({ leadId, items, reload }) {
    const [f, setF] = useState({ date: "", type: "email", notes: "", reminder: true });
    const [busy, setBusy] = useState(false);
    const add = async (e) => {
        e.preventDefault();
        if (!f.date) return toast.error("Date required");
        setBusy(true);
        try {
            await api.post("/followups", { lead_id: leadId, ...f, date: new Date(f.date).toISOString() });
            toast.success("Follow-up scheduled");
            setF({ date: "", type: "email", notes: "", reminder: true });
            reload();
        } catch { toast.error("Failed to add"); }
        setBusy(false);
    };
    const complete = async (fu) => {
        await api.patch(`/followups/${fu.id}`, { status: "completed" });
        toast.success("Marked completed");
        reload();
    };
    const remove = async (fu) => {
        await api.delete(`/followups/${fu.id}`);
        reload();
    };
    return (
        <div className="space-y-4">
            <form onSubmit={add} className="grid grid-cols-2 gap-2 p-3 border border-border rounded-md bg-secondary/20">
                <input type="datetime-local" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inputCls} />
                <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={inputCls}>
                    {["email", "call", "whatsapp", "meeting"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <input placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={`${inputCls} col-span-2`} />
                <button disabled={busy} className="col-span-2 h-10 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">
                    {busy ? "Adding…" : "Add follow-up"}
                </button>
            </form>
            {items.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No follow-ups yet.</div>}
            {items.map((fu) => (
                <div key={fu.id} className="border border-border rounded-md p-3 flex items-start gap-3">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground border border-border rounded px-2 py-0.5">{fu.type}</span>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm">{fu.notes || "—"}</div>
                        <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{new Date(fu.date).toLocaleString()}</div>
                    </div>
                    <span className={`text-[10px] font-semibold uppercase tracking-wider ${fu.status === "completed" ? "text-success" : fu.status === "missed" ? "text-destructive" : "text-warning"}`}>{fu.status}</span>
                    {fu.status !== "completed" && (
                        <button onClick={() => complete(fu)} className="text-xs text-primary hover:underline">Done</button>
                    )}
                    <button onClick={() => remove(fu)} className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                </div>
            ))}
        </div>
    );
}

function ProposalsTab({ leadId, items, reload }) {
    const [p, setP] = useState({ proposal_date: new Date().toISOString().slice(0, 10), amount: 0, status: "draft", notes: "" });
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            let file_url = null, file_name = null;
            if (file) {
                const fd = new FormData();
                fd.append("file", file);
                const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                file_url = up.data.file_url; file_name = up.data.file_name;
            }
            await api.post("/proposals", {
                lead_id: leadId, proposal_date: new Date(p.proposal_date).toISOString(),
                amount: Number(p.amount) || 0, status: p.status, notes: p.notes, file_url, file_name,
            });
            toast.success("Proposal added");
            setP({ proposal_date: new Date().toISOString().slice(0, 10), amount: 0, status: "draft", notes: "" });
            setFile(null);
            reload();
        } catch { toast.error("Failed to add"); }
        setBusy(false);
    };
    return (
        <div className="space-y-4">
            <form onSubmit={submit} className="grid grid-cols-2 gap-2 p-3 border border-border rounded-md bg-secondary/20">
                <input type="date" required value={p.proposal_date} onChange={(e) => setP({ ...p, proposal_date: e.target.value })} className={inputCls} />
                <input type="number" placeholder="Amount" value={p.amount} onChange={(e) => setP({ ...p, amount: e.target.value })} className={inputCls} />
                <select value={p.status} onChange={(e) => setP({ ...p, status: e.target.value })} className={inputCls}>
                    {["draft", "sent", "accepted", "rejected"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" accept=".pdf,.doc,.docx" />
                <input placeholder="Notes" value={p.notes} onChange={(e) => setP({ ...p, notes: e.target.value })} className={`${inputCls} col-span-2`} />
                <button disabled={busy} className="col-span-2 h-10 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">
                    {busy ? "Saving…" : "Add proposal"}
                </button>
            </form>
            {items.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No proposals yet.</div>}
            {items.map((pr) => (
                <div key={pr.id} className="border border-border rounded-md p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">${Number(pr.amount).toLocaleString()}</div>
                        <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{new Date(pr.proposal_date).toLocaleDateString()} · {pr.status}</div>
                        {pr.notes && <div className="text-xs text-muted-foreground mt-1">{pr.notes}</div>}
                    </div>
                    {pr.file_url && (
                        <a href={backendUrl(pr.file_url)} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">
                            {pr.file_name || "File"}
                        </a>
                    )}
                </div>
            ))}
        </div>
    );
}

function ActivityTab({ items }) {
    if (items.length === 0) return <div className="text-sm text-muted-foreground text-center py-6">No activity yet.</div>;
    return (
        <div className="relative pl-6">
            <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />
            {items.map((a) => (
                <div key={a.id} className="relative py-3">
                    <span className="absolute -left-4 top-4 h-2 w-2 rounded-full bg-primary ring-4 ring-background" />
                    <div className="text-sm">{a.description}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">{new Date(a.created_at).toLocaleString()} · {a.actor_name}</div>
                </div>
            ))}
        </div>
    );
}

function ConvertDialog({ lead, users, onClose, onDone }) {
    const [form, setForm] = useState({
        project_name: `${lead.company} - ${lead.service || "Project"}`,
        deadline: "", budget: lead.value || 0, notes: "", team_members: [],
    });
    const [busy, setBusy] = useState(false);
    const toggleMember = (id) => {
        setForm((f) => f.team_members.includes(id)
            ? { ...f, team_members: f.team_members.filter((x) => x !== id) }
            : { ...f, team_members: [...f.team_members, id] });
    };
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post(`/leads/${lead.id}/convert`, {
                project_name: form.project_name,
                deadline: form.deadline ? new Date(form.deadline).toISOString() : undefined,
                budget: Number(form.budget) || 0,
                notes: form.notes,
                team_members: form.team_members,
            });
            onDone();
        } catch { toast.error("Conversion failed"); }
        setBusy(false);
    };
    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-background/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <motion.form
                initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 12, opacity: 0 }}
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg bg-card border border-border rounded-md shadow-xl"
            >
                <div className="p-5 border-b border-border">
                    <h3 className="font-heading text-xl font-semibold">Convert to Project</h3>
                    <p className="text-sm text-muted-foreground mt-1">Create a client &amp; kick off a new project.</p>
                </div>
                <div className="p-5 space-y-4">
                    <Field label="Project Name"><input required className={inputCls} value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} /></Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Deadline"><input type="date" className={inputCls} value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} /></Field>
                        <Field label="Budget"><input type="number" className={inputCls} value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} /></Field>
                    </div>
                    <Field label="Team">
                        <div className="flex flex-wrap gap-2">
                            {users.map((u) => (
                                <button
                                    type="button"
                                    key={u.id}
                                    onClick={() => toggleMember(u.id)}
                                    className={`h-8 px-3 rounded-full text-xs border transition-colors ${
                                        form.team_members.includes(u.id)
                                            ? "bg-primary text-primary-foreground border-primary"
                                            : "border-border hover:bg-secondary"
                                    }`}
                                >{u.name}</button>
                            ))}
                        </div>
                    </Field>
                    <Field label="Notes"><textarea rows={2} className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
                </div>
                <div className="flex justify-end gap-2 p-4 border-t border-border">
                    <button type="button" onClick={onClose} className="h-10 px-4 rounded-md border border-border hover:bg-secondary text-sm">Cancel</button>
                    <button disabled={busy} className="h-10 px-4 rounded-md bg-success text-white text-sm font-medium hover:bg-success/90 transition-colors disabled:opacity-60">
                        {busy ? "Converting…" : "Convert"}
                    </button>
                </div>
            </motion.form>
        </motion.div>
    );
}
